import { useState } from "react";
import PennText from "@/components/ui/text/PennText";
import { PennButton } from "@/components/ui";
import { useAppSelector } from "@/stores/hooks";
import { Cad3dPanel } from "@/components/cad3d/Cad3dPanel";
import { AttachmentsPanel } from "@/components/attachments/AttachmentsPanel";
import { useFinalizeQuote, useTask } from "@/hooks/useTasks";
import { useApprovalState, useDecideApproval } from "@/hooks/useApprovals";
import type { ApprovalState } from "@/api/approvals";
import type { TaskDetail } from "@/api/tasks";

/** One fastener row shown in an application's RFQ-context fastener table. */
export type TaskFastenerRow = {
  brand: string | null;
  type: string | null;
  // Single-side quantity. Persisted on its own when only one side is used; the
  // A/B columns stay null in that case, so the table reads it as the A-side.
  qtyPerPart: number | null;
  qtyPerPartA: number | null;
  qtyPerPartB: number | null;
};

/**
 * One application carried into the task side panel. `savedId` is the live
 * application UUID — CAD preview + files only render when it is present (ad-hoc
 * apps never saved to the library have no server-side artifacts). The metadata
 * + fasteners mirror the POC RFQ-context card (App No · OEM · Program · Material
 * · Annual volume + the fastener table).
 */
export type TaskApplicationRef = {
  savedId: string | null;
  name: string;
  applicationNumber?: string | null;
  oem?: string | null;
  productionProgram?: string | null;
  materialCode?: string | null;
  annualVolume?: number | null;
  batchSize?: number | null;
  fasteners?: TaskFastenerRow[];
};

/** Broken-out customer block (POC parity) — Company / Contact / Email / Phone. */
export type TaskCustomerDetail = {
  companyName?: string | null;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
};

export type TaskContext = {
  /** The open task's id. When set, Configure persists manual line items to the
   * task (D4) and the side panel surfaces the live approval + finalize actions. */
  taskId?: string;
  meta?: string;
  /** Legacy single-line customer; superseded by `customerDetail` when available. */
  customer?: string;
  /** POC-parity broken-out customer block (staff-readable task detail). */
  customerDetail?: TaskCustomerDetail;
  note?: string;
  workflow?: string[];
  /** Legacy display-only names; superseded by `applicationRefs` when available. */
  applications?: string[];
  /** Issue 3 (POC parity) — per-app CAD preview + Files/Download-all + metadata
   * + fasteners in the task panel. Carries the saved id so the read-only
   * artifacts can be fetched. */
  applicationRefs?: TaskApplicationRef[];
};

type TaskSidePanelProps = {
  task: TaskContext;
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Normalize a task's lifecycle status to the POC's open/quoted vocabulary. */
function deriveStatus(t: TaskDetail): "open" | "quoted" {
  if (t.quotedAt || t.workflowQuoteStatus === "quoted" || t.status === "quoted") {
    return "quoted";
  }
  return "open";
}

/** HTTP status off an unknown (axios) error, for friendly gate messaging. */
function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } } | null)?.response?.status;
}

/** Service error code from the `{ error: { code } }` envelope (svc-06/07). */
function errorCode(err: unknown): string | undefined {
  return (
    err as { response?: { data?: { error?: { code?: string } } } } | null
  )?.response?.data?.error?.code;
}

type ApprovalTone = "green" | "amber" | "red" | "slate";

const TONE_CLASS: Record<ApprovalTone, string> = {
  green: "bg-emerald-100 text-emerald-800",
  amber: "bg-amber-100 text-amber-800",
  red: "bg-red-100 text-red-800",
  slate: "bg-slate-100 text-slate-700",
};

/** One-line approval status + tone for the panel header chip (D3). */
function approvalSummary(a: ApprovalState | null | undefined): {
  label: string;
  tone: ApprovalTone;
} {
  if (!a) return { label: "Not started", tone: "slate" };
  if (a.rejected) return { label: "Rejected", tone: "red" };
  if (a.complete) return { label: "Approved", tone: "green" };
  if (a.currentAssignment) {
    return { label: `Awaiting ${a.currentAssignment.stepName}`, tone: "amber" };
  }
  if (a.assignments.length === 0) return { label: "Not started", tone: "slate" };
  return { label: "In progress", tone: "amber" };
}

/**
 * Fixed approval steps per flow — mirrors svc-07 `steps.config.ts`. Lets the
 * panel show ALL steps of the chosen flow upfront (POC parity: both
 * Engineering-Review + Sales-Sign-off badges visible from the start, MD-05),
 * not only the steps already reached. `engineer` = 2 steps; `sales` = 1 step
 * (a sales creator self-signs-off, since sales are qualified engineers).
 */
const FLOW_STEPS: Record<
  ApprovalState["flowType"],
  ReadonlyArray<{ stepOrder: number; stepName: string }>
> = {
  engineer: [
    { stepOrder: 1, stepName: "Engineering Review" },
    { stepOrder: 2, stepName: "Sales Sign-off" },
  ],
  sales: [{ stepOrder: 1, stepName: "Sales Sign-off" }],
};

type FlowStepStatus =
  | "approved"
  | "auto-approved"
  | "rejected"
  | "current"
  | "upcoming"
  | "not-applicable";

const STEP_BADGE_CLASS: Record<FlowStepStatus, string> = {
  approved: "bg-emerald-100 text-emerald-700",
  "auto-approved": "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  current: "bg-amber-100 text-amber-700",
  upcoming: "bg-slate-100 text-slate-500",
  "not-applicable": "bg-slate-100 text-slate-400",
};

const STEP_STATUS_LABEL: Record<FlowStepStatus, string> = {
  approved: "approved",
  "auto-approved": "auto-approved",
  rejected: "rejected",
  current: "awaiting decision",
  upcoming: "upcoming",
  "not-applicable": "not applicable",
};

/** Merge the fixed flow steps with the live assignment state so every step is
 * shown with its status (approved / rejected / current / upcoming).
 * PENN-095 #3: once a step is REJECTED the flow stops, so every later step is
 * "not applicable" (never "upcoming") — e.g. an engineer rejection makes the
 * downstream Sales Sign-off Not applicable rather than implying it is still to
 * come. A rejection at the final (sales) step just shows as "rejected". */
function flowStepViews(
  approval: ApprovalState | null | undefined,
): Array<{ stepOrder: number; stepName: string; status: FlowStepStatus }> {
  if (!approval) return [];
  const defs = FLOW_STEPS[approval.flowType] ?? FLOW_STEPS.engineer;
  const byOrder = new Map(approval.assignments.map((a) => [a.stepOrder, a]));
  const currentOrder = approval.currentAssignment?.stepOrder ?? null;
  let rejectedBefore = false;
  return defs.map((d) => {
    const a = byOrder.get(d.stepOrder);
    let status: FlowStepStatus;
    if (a?.decision === "reject") status = "rejected";
    else if (a?.decision === "auto_approve") status = "auto-approved";
    else if (a?.decidedAt) status = "approved";
    else if (rejectedBefore) status = "not-applicable";
    else if (currentOrder === d.stepOrder) status = "current";
    else status = "upcoming";
    if (status === "rejected") rejectedBefore = true;
    return { stepOrder: d.stepOrder, stepName: d.stepName, status };
  });
}

/** Friendly status label + tone for the compact POC-style Workflow summary. */
function reviewStatusView(
  stepView: { status: FlowStepStatus } | undefined,
  rawStatus: string | null | undefined,
): { label: string; tone: ApprovalTone } {
  if (stepView) {
    switch (stepView.status) {
      case "approved":
      case "auto-approved":
        return { label: "Approved", tone: "green" };
      case "rejected":
        return { label: "Rejected", tone: "red" };
      case "current":
        return { label: "In review", tone: "amber" };
      default:
        return { label: "Pending", tone: "slate" };
    }
  }
  const s = (rawStatus ?? "").toLowerCase();
  if (s.includes("approv")) return { label: "Approved", tone: "green" };
  if (s.includes("reject")) return { label: "Rejected", tone: "red" };
  return { label: "Pending", tone: "slate" };
}

/** Locale-grouped number, em-dash when absent. */
function fmtNum(n: number | null | undefined): string {
  return n == null ? "—" : n.toLocaleString();
}

/** Truthy text or em-dash. */
function orDash(s: string | null | undefined): string {
  return s && s.trim().length > 0 ? s : "—";
}

/**
 * Whether the current user may decide the task's current pending step. Mirrors
 * svc-07 `canActOnStep` EXACTLY so a shown button never 403s.
 */
function canDecideCurrentStep(
  approval: ApprovalState | null | undefined,
  role:
    | { code?: string; canApproveEngineering?: boolean; canManageUsers?: boolean }
    | null
    | undefined,
  task: TaskDetail | undefined,
  userId: string | null | undefined,
): boolean {
  const step = approval?.currentAssignment;
  if (!step || !role || approval?.complete || approval?.rejected) return false;
  if (role.canManageUsers) return true;
  if (step.requiredRoleCode === "engineering") {
    return !!role.canApproveEngineering;
  }
  // Mirrors svc-07: the caller's role must MATCH the step's required role
  // (data-driven — no user-role literal, so custom roles behave identically
  // on both sides).
  if (role.code === step.requiredRoleCode) return true;
  if (step.requiredRoleCode === "sales") {
    if (approval?.flowType === "sales" && !!task?.createdBy && task.createdBy === userId) {
      return true;
    }
  }
  return false;
}

export const TaskSidePanel = ({ task }: TaskSidePanelProps) => {
  // POC parity (renderTaskSidePanel gates the CAD/Files blocks on
  // `canCreateQuotation()`): the 3D preview + files are internal-only artifacts.
  const role = useAppSelector((s) => s.auth.user?.role ?? null);
  const userId = useAppSelector((s) => s.auth.user?.id ?? null);
  const canGenerateQuote = !!role?.canGenerateQuote;

  const refs = task.applicationRefs ?? null;
  const names = task.applications ?? [];

  // Live approval + finalize for the open task (POC keeps these in the Configure
  // task context, not a separate drawer). Hooks no-op when there's no taskId.
  const taskId = task.taskId ?? null;
  const { data: taskDetail } = useTask(taskId);
  const { data: approval } = useApprovalState(taskId);
  const decide = useDecideApproval();
  const finalize = useFinalizeQuote();
  const [actionError, setActionError] = useState<string | null>(null);
  const [finalizeMsg, setFinalizeMsg] = useState<string | null>(null);

  const status = approvalSummary(approval);
  const stepViews = flowStepViews(approval);
  const canDecide = canDecideCurrentStep(approval, role, taskDetail, userId);
  const alreadyQuoted = !!taskDetail && deriveStatus(taskDetail) === "quoted";

  // PENN-083: Finalize Quote is enabled for ALL roles only once the approval
  // flow is fully complete — Engineering AND Sales for the engineer flow, Sales
  // sign-off for the sales flow (`approval.complete` already encodes the flow's
  // required steps) — and not rejected. svc-06 quote-finalize enforces the same
  // rule server-side (409 awaiting-approval / approval-rejected); this mirrors it
  // in the UI so the button can't be clicked before both approvals are provided.
  // While approval state is still loading (`approval == null`) we fail closed.
  const approvalsComplete =
    approval != null && approval.complete === true && approval.rejected !== true;

  // Compact POC-style Workflow summary (Engineering Review + Quoted), shown
  // above our richer per-step Approval + Quote sections. The review row tracks
  // the flow's first review step (Engineering Review for the engineer flow,
  // Sales Sign-off for the sales flow); the quoted row tracks finalize.
  // Sales-flow tasks never read the eng-review column — svc-07 no longer
  // stamps it for them (bug-061 / R-4).
  const isSalesFlow =
    (approval?.flowType ?? taskDetail?.flowType) === "sales";
  const reviewStep =
    stepViews.find((s) => s.stepName === "Engineering Review") ?? stepViews[0];
  const reviewLabel =
    reviewStep?.stepName ?? (isSalesFlow ? "Sales Sign-off" : "Engineering Review");
  const reviewView = reviewStatusView(
    reviewStep,
    isSalesFlow ? null : taskDetail?.workflowEngReviewStatus,
  );
  // PENN-095 #3: a rejected request will never be quoted, so the Quoted row reads
  // "Not applicable" (not "Pending") once the approval flow has been rejected —
  // whether the rejection came from Engineering or Sales.
  const quotedView: { label: string; tone: ApprovalTone } = alreadyQuoted
    ? { label: "Quoted", tone: "green" }
    : approval?.rejected
      ? { label: "Not applicable", tone: "slate" }
      : { label: "Pending", tone: "slate" };

  const cust = task.customerDetail;
  const hasCustomerDetail =
    !!cust &&
    [cust.companyName, cust.contactName, cust.email, cust.phone, cust.address].some(
      (v) => v && String(v).trim().length > 0,
    );

  const onDecide = (decision: "approve" | "reject") => {
    if (!taskId) return;
    setActionError(null);
    decide.mutate(
      { taskId, body: { decision } },
      {
        onError: (err) =>
          setActionError(
            httpStatus(err) === 403
              ? "You're not permitted to decide this step."
              : "Could not record your decision. Please try again.",
          ),
      },
    );
  };

  const onFinalize = () => {
    if (!taskId) return;
    setActionError(null);
    setFinalizeMsg(null);
    finalize.mutate(
      { taskId },
      {
        onSuccess: () => setFinalizeMsg("Quote finalized."),
        onError: (err) => {
          const code = errorCode(err);
          if (code === "approval-rejected") {
            setActionError(
              "This quote was rejected in review, so it can't be sent. Reopen the task or start a new request.",
            );
          } else if (httpStatus(err) === 409) {
            setActionError(
              "This quote is awaiting approval and can't be finalized yet — complete the engineering/sales review first.",
            );
          } else {
            setActionError("Could not finalize the quote. Please try again.");
          }
        },
      },
    );
  };

  return (
    // Height matches the Configure card (max-h-[min(92vh,960px)]) so the panel
    // never runs off-screen; overflow lives in the scrollable body below (SS#1).
    <div className="w-full lg:w-[420px] flex-shrink-0 rounded-2xl bg-white shadow-soft border border-gray-200 overflow-hidden flex flex-col max-h-[min(92vh,960px)]">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
        <PennText as="span" size="xs" color="slate-500" className="uppercase">
          RFQ context
        </PennText>
        <PennText as="h3" size="lg" color="slate-900" font="semibold" className="mt-0.5">
          Customer &amp; Application Details
        </PennText>
        {task.meta && (
          <PennText as="p" size="xs" color="slate-500" className="mt-0.5">
            {task.meta}
          </PennText>
        )}
      </div>

      {/* Body — min-h-0 lets this flex child shrink below its content height so
          overflow-y-auto actually engages; without it the panel grows past the
          root max-h and runs off-screen / taller than the Configure card (SS#1). */}
      <div className="p-5 space-y-5 overflow-y-auto flex-1 min-h-0">
        <Section label="Customer">
          {hasCustomerDetail && cust ? (
            <div className="space-y-1">
              {cust.companyName && (
                <PennText as="p" size="sm" color="slate-900" font="semibold">
                  {cust.companyName}
                </PennText>
              )}
              {cust.contactName && (
                <PennText as="p" size="sm" color="slate-500">{cust.contactName}</PennText>
              )}
              {cust.address && (
                <PennText as="p" size="sm" color="slate-500" className="whitespace-pre-wrap">
                  {cust.address}
                </PennText>
              )}
              {cust.email && (
                <DetailRow label="Email" value={cust.email} />
              )}
              {cust.phone && (
                <DetailRow label="Phone" value={cust.phone} />
              )}
            </div>
          ) : (
            <PennText as="p" size="sm" color="slate-500">{task.customer ?? "—"}</PennText>
          )}
        </Section>

        <Section label="RFQ Note">
          <PennText as="p" size="sm" color="slate-500" className="whitespace-pre-wrap">
            {task.note ?? "—"}
          </PennText>
        </Section>

        <Section label="Workflow">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <PennText as="span" size="sm" color="slate-500">{reviewLabel}</PennText>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${TONE_CLASS[reviewView.tone]}`}
              >
                {reviewView.label}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <PennText as="span" size="sm" color="slate-500">Quoted</PennText>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${TONE_CLASS[quotedView.tone]}`}
              >
                {quotedView.label}
              </span>
            </div>
          </div>
        </Section>

        <Section label="Applications">
          {refs && refs.length > 0 ? (
            <div className="space-y-3">
              {refs.map((ref, i) => (
                <div
                  key={ref.savedId ?? `${ref.name}-${i}`}
                  className="px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 space-y-3"
                >
                  <div>
                    <PennText as="p" size="sm" color="slate-900" font="semibold">{ref.name}</PennText>
                    <AppMetaLine ref_={ref} />
                  </div>
                  {ref.savedId && canGenerateQuote && (
                    <>
                      {/* 3D part card (POC SS#2): status + filename + the
                          "Analyze 3D part" action, which opens the 2-pane
                          comparison screen (/cad/compare), not the single
                          viewer. */}
                      <div className="rounded-xl border border-gray-200 bg-white p-3">
                        <PennText
                          as="p"
                          size="xs"
                          color="slate-500"
                          className="uppercase mb-1.5"
                        >
                          3D part
                        </PennText>
                        <Cad3dPanel
                          applicationId={ref.savedId}
                          readOnly
                          showViewer={false}
                          viewerLinkLabel="Analyze 3D part"
                          viewerLinkPath="/cad/compare"
                          viewerLinkAsButton
                          viewerLinkNewWindow
                        />
                      </div>
                      <div className="rounded-xl border border-gray-200 bg-white p-3">
                        <PennText
                          as="p"
                          size="xs"
                          color="slate-500"
                          className="uppercase mb-1.5"
                        >
                          Files
                        </PennText>
                        <AttachmentsPanel applicationId={ref.savedId} readOnly />
                      </div>
                    </>
                  )}
                  {ref.fasteners && ref.fasteners.length > 0 && (
                    <div>
                      <PennText
                        as="p"
                        size="xs"
                        color="slate-500"
                        className="uppercase mb-1.5"
                      >
                        Fasteners
                      </PennText>
                      <FastenerTable rows={ref.fasteners} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : names.length > 0 ? (
            <div className="space-y-2">
              {names.map((app, i) => (
                <div key={i} className="px-3 py-2 rounded-xl border border-gray-200 bg-gray-50">
                  <PennText as="p" size="sm" color="slate-900">{app}</PennText>
                </div>
              ))}
            </div>
          ) : (
            <PennText as="p" size="sm" color="slate-500">—</PennText>
          )}
        </Section>

        {/* Approval (D3) — POC keeps the engineering/sales approval action inside
            the Configure task context. Only renders for an open task. */}
        {taskId && (
          <section className="border border-gray-200 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <PennText as="h4" size="sm" color="slate-900" font="semibold">Approval</PennText>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${TONE_CLASS[status.tone]}`}
              >
                {status.label}
              </span>
            </div>
            {/* POC parity (MD-05): show EVERY step of the flow as a badge from
                the start — Engineering Review + Sales Sign-off for the engineer
                flow, Sales Sign-off only for the sales flow — each with its live
                status, so reviewers see the full two-stage flow, not just the
                step reached so far. */}
            {stepViews.length > 0 && (
              <ul className="space-y-1">
                {stepViews.map((s) => (
                  <li key={s.stepOrder} className="flex items-center gap-2 text-sm text-slate-600">
                    <span
                      className={`flex-shrink-0 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${STEP_BADGE_CLASS[s.status]}`}
                    >
                      {s.stepOrder}
                    </span>
                    <span className="font-medium">{s.stepName}</span>
                    <span className="text-slate-400">{STEP_STATUS_LABEL[s.status]}</span>
                  </li>
                ))}
              </ul>
            )}
            {canDecide && (
              <div className="flex items-center gap-2 pt-1">
                <PennButton variant="primary" size="sm" onClick={() => onDecide("approve")} disabled={decide.isPending}>
                  {decide.isPending ? "Saving…" : "Approve"}
                </PennButton>
                <PennButton variant="secondary" size="sm" onClick={() => onDecide("reject")} disabled={decide.isPending}>
                  Reject
                </PennButton>
              </div>
            )}
          </section>
        )}

        {/* Quote / Finalize (D5) — internal, approval-gated */}
        {taskId && canGenerateQuote && (
          <section className="border border-gray-200 rounded-2xl p-4 space-y-2">
            <PennText as="h4" size="sm" color="slate-900" font="semibold">Quote</PennText>
            {alreadyQuoted ? (
              <PennText as="p" size="sm" color="slate-500">
                Quoted{taskDetail?.quotedAt ? ` on ${formatDate(taskDetail.quotedAt)}` : ""}
                {taskDetail?.quotedByEmail ? ` by ${taskDetail.quotedByEmail}` : ""}.
              </PennText>
            ) : (
              <>
                <PennText as="p" size="xs" color="slate-500">
                  Stamps this task Quoted.
                </PennText>
                <PennButton
                  variant="primary"
                  size="sm"
                  onClick={onFinalize}
                  disabled={finalize.isPending || !approvalsComplete}
                >
                  {finalize.isPending ? "Finalizing…" : "Finalize quote"}
                </PennButton>
                {!approvalsComplete && (
                  <PennText as="p" size="xs" color="slate-500">
                    {approval?.rejected
                      ? "This request was rejected in review, so it can’t be finalized."
                      : "Available once Engineering and Sales approvals are complete."}
                  </PennText>
                )}
              </>
            )}
            {finalizeMsg && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2">
                <PennText as="p" size="xs" color="slate-900">{finalizeMsg}</PennText>
              </div>
            )}
          </section>
        )}

        {actionError && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
            <PennText as="p" size="sm" color="slate-900">{actionError}</PennText>
          </div>
        )}
      </div>
    </div>
  );
};

const Section = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <PennText as="p" size="xs" color="slate-500" font="semibold" className="uppercase mb-2">
      {label}
    </PennText>
    {children}
  </div>
);

/** Label: value row used in the broken-out Customer block. */
const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-baseline gap-2">
    <PennText as="span" size="xs" color="slate-500" className="flex-shrink-0">
      {label}:
    </PennText>
    <PennText as="span" size="sm" color="slate-900" className="break-all">
      {value}
    </PennText>
  </div>
);

/** One-line application metadata (App No · OEM · Program · Material · Annual volume). */
const AppMetaLine = ({ ref_ }: { ref_: TaskApplicationRef }) => {
  const parts: string[] = [];
  if (ref_.applicationNumber) parts.push(`App No.: ${ref_.applicationNumber}`);
  if (ref_.oem) parts.push(`OEM: ${ref_.oem}`);
  if (ref_.productionProgram) parts.push(`Program: ${ref_.productionProgram}`);
  if (ref_.materialCode) parts.push(`Material: ${ref_.materialCode}`);
  if (ref_.annualVolume != null) parts.push(`Annual volume: ${fmtNum(ref_.annualVolume)}`);
  if (ref_.batchSize != null) parts.push(`Batch: ${fmtNum(ref_.batchSize)}`);
  if (parts.length === 0) return null;
  return (
    <PennText as="p" size="xs" color="slate-500" className="mt-0.5">
      {parts.join("  •  ")}
    </PennText>
  );
};

/** Compact fastener table (Brand / Type / Qty per part A/B) — POC RFQ-context. */
const FastenerTable = ({ rows }: { rows: TaskFastenerRow[] }) => (
  <div className="overflow-hidden rounded-lg border border-gray-200">
    <table className="w-full text-xs">
      <thead>
        <tr className="bg-gray-100 text-slate-500">
          <th className="px-2 py-1 text-left font-semibold">Brand</th>
          <th className="px-2 py-1 text-left font-semibold">Type</th>
          <th className="px-2 py-1 text-right font-semibold">Qty/part (A/B)</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((f, i) => (
          <tr key={`${f.brand ?? "—"}-${f.type ?? i}-${i}`} className="border-t border-gray-100">
            <td className="px-2 py-1 text-slate-900">{orDash(f.brand)}</td>
            <td className="px-2 py-1 text-slate-700 break-all">{orDash(f.type)}</td>
            <td className="px-2 py-1 text-right text-slate-700 whitespace-nowrap">
              {fmtNum(f.qtyPerPartA ?? f.qtyPerPart)} / {fmtNum(f.qtyPerPartB)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
